import type {
  Celda,
  Columna,
  DocumentoExportable,
  FilaExportable,
  Indicador,
  Seccion,
} from "@/componentes/exportar/modelo"
import { monto } from "@/componentes/tesoreria/dinero"
import { comoFecha } from "@/lib/fechas"

import { CLASE } from "./Listas"
import { esOperativa, type Partida, type Resumen, sumaUsd } from "./tipos"

/**
 * El Resumen Macro entero, listo para «Exportar» (29/10; rediseñado el 30/10).
 *
 * ⚠ **Se exporta el resumen COMPLETO, no sólo la lista que se tenga abierta abajo.** Las vistas
 * que se leen en vivo (caja chica, facturación, planificador) llevan su propio botón.
 *
 * ⚠ **Se arma con los datos y no leyendo la tabla**: el «Monto» mezcla dólares, bolívares y USDT,
 * y leído de la pantalla quedaría en texto.
 *
 * ⚠ **Toda columna de dólares cuadra con su total** (pedido del usuario: la totalización se
 * prestaba a confusión). Lo que suma y lo que no van en DOS columnas: con una sola, sumar la
 * columna a mano no daba el total del pie. El monto original no se totaliza porque mezcla monedas;
 * «Cómo se totalizan los bancos» dice moneda por moneda cómo se llega a los dólares.
 *
 * ⚠ **El cuadro de alcance se lee como una resta, de arriba abajo**, y no lleva total al pie
 * (`cuadro`): `conTotales` le sumaba reserva, compromisos y hasta la cobertura en un «Total» falso.
 */

const USD_SUMA: Columna = { titulo: "USD que suma", tipo: "monto" }
const USD_NO_SUMA: Columna = { titulo: "USD que no suma", tipo: "monto" }

const COLUMNAS_BASE: Columna[] = [
  { titulo: "Qué", tipo: "texto" },
  { titulo: "Detalle", tipo: "texto" },
  { titulo: "Exp.", tipo: "texto" },
  { titulo: "Vence", tipo: "fecha" },
  { titulo: "Moneda", tipo: "texto" },
  { titulo: "Monto", tipo: "monto" },
  USD_SUMA,
  USD_NO_SUMA,
]

const dolares = (v: number) => `$ ${monto(v.toFixed(2))}`
const queSuman = (ps: Partida[]) => ps.filter((p) => p.suma)
const queNoSuman = (ps: Partida[]) => ps.filter((p) => !p.suma)

function detalleDe(p: Partida): string | null {
  const partes = [
    CLASE[p.clase],
    p.detalle,
    p.nota,
    p.usd === null ? "sin tasa: no entra en el total" : null,
  ].filter(Boolean)
  return partes.length ? partes.join(" · ") : null
}

const usdDe = (p: Partida) => (p.usd === null ? null : Number(p.usd))

function fila(p: Partida, conOrden: boolean): FilaExportable {
  return {
    celdas: [
      p.titulo,
      detalleDe(p),
      p.expediente,
      p.fecha?.slice(0, 10) ?? null,
      p.moneda,
      Number(p.monto),
      p.suma ? usdDe(p) : null,
      p.suma ? null : usdDe(p),
      ...(conOrden ? [p.orden] : []),
    ],
  }
}

/** La fila de totales: las dos columnas de dólares, y nada más. */
function pieDe(columnas: Columna[], partidas: Partida[]): Celda[][] {
  return [
    columnas.map((c, i) => {
      if (i === 0) return "Total"
      if (c === USD_SUMA) return sumaUsd(queSuman(partidas))
      if (c === USD_NO_SUMA) return sumaUsd(queNoSuman(partidas))
      return null
    }),
  ]
}

function seccionPlana(titulo: string, partidas: Partida[]): Seccion {
  return {
    titulo,
    columnas: COLUMNAS_BASE,
    filas: partidas.map((p) => fila(p, false)),
    pie: pieDe(COLUMNAS_BASE, partidas),
  }
}

/** Agrupada como en la pantalla, y cada rótulo dice si ese grupo suma o no. */
function seccionAgrupada(
  titulo: string,
  partidas: Partida[],
  grupos: Record<string, string>,
  campo: "estado" | "clase",
  conOrden: boolean,
): Seccion {
  const columnas = conOrden
    ? [...COLUMNAS_BASE, { titulo: "Orden", tipo: "texto" } as Columna]
    : COLUMNAS_BASE
  const vacias = columnas.slice(1).map(() => null)
  const filas: FilaExportable[] = []
  for (const g of Object.keys(grupos)) {
    const ps = partidas.filter((p) => (p[campo] ?? "") === g)
    if (ps.length === 0) continue
    const suman = sumaUsd(queSuman(ps))
    const noSuman = sumaUsd(queNoSuman(ps))
    const cuanto = [
      queSuman(ps).length ? `suma ${dolares(suman)}` : null,
      queNoSuman(ps).length ? `no suma ${dolares(noSuman)}` : null,
    ]
      .filter(Boolean)
      .join(" · ")
    filas.push({ celdas: [`${grupos[g]} · ${ps.length} · ${cuanto}`, ...vacias], grupo: true })
    for (const p of ps) filas.push(fila(p, conOrden))
  }
  return { titulo, columnas, filas, pie: pieDe(columnas, partidas) }
}

const ORDEN_MONEDAS = ["USD", "USDT", "EUR", "BS"]

function monedasDe(partidas: Partida[]): string[] {
  const hay = [...new Set(partidas.map((p) => p.moneda))]
  return [
    ...ORDEN_MONEDAS.filter((m) => hay.includes(m)),
    ...hay.filter((m) => !ORDEN_MONEDAS.includes(m)),
  ]
}

const enCentimos = (ps: Partida[]) =>
  ps.reduce((t, p) => t + Math.round(Number(p.monto) * 100), 0) / 100

/** De qué manera llega cada moneda a los dólares: lo que el total de bancos da por sabido. */
function conversionDe(r: Resumen, moneda: string): string {
  if (moneda === "USD") return "Dólares"
  if (moneda === "USDT") return "1:1 con el dólar"
  if (moneda === "BS") {
    const tasa = r.tasas_bs?.find((t) => t.codigo === r.tasa_bs)
    return tasa ? `${tasa.nombre}: ${monto(tasa.valor)} Bs` : "Tasa del día"
  }
  return moneda === "EUR" ? "Euro a su cruce BCV" : "Tasa del día"
}

function seccionComposicion(r: Resumen): Seccion {
  const columnas: Columna[] = [
    { titulo: "Moneda", tipo: "texto" },
    { titulo: "Cuentas", tipo: "numero" },
    { titulo: "Saldo en su moneda", tipo: "monto" },
    { titulo: "Cómo pasa a dólares", tipo: "texto" },
    USD_SUMA,
    USD_NO_SUMA,
  ]
  const filas: FilaExportable[] = []
  for (const moneda of monedasDe(r.bancos.partidas)) {
    const ps = r.bancos.partidas.filter((p) => p.moneda === moneda)
    for (const [grupo, suma] of [
      [queSuman(ps), true],
      [queNoSuman(ps), false],
    ] as const) {
      if (grupo.length === 0) continue
      filas.push({
        celdas: [
          suma ? moneda : `${moneda} · copias del 3.0`,
          grupo.length,
          enCentimos(grupo),
          conversionDe(r, moneda),
          suma ? sumaUsd(grupo) : null,
          suma ? null : sumaUsd(grupo),
        ],
      })
    }
  }
  return {
    titulo: "Cómo se totalizan los bancos",
    cuadro: true,
    columnas,
    filas,
    pie: [pieTotalBancos(r)],
  }
}

function pieTotalBancos(r: Resumen): Celda[] {
  return [
    "Total",
    r.bancos.partidas.length,
    null,
    null,
    sumaUsd(queSuman(r.bancos.partidas)),
    sumaUsd(queNoSuman(r.bancos.partidas)),
  ]
}

function seccionBancos(r: Resumen): Seccion {
  const columnas: Columna[] = [
    { titulo: "Cuenta", tipo: "texto" },
    { titulo: "Detalle", tipo: "texto" },
    { titulo: "Moneda", tipo: "texto" },
    { titulo: "Saldo", tipo: "monto" },
    USD_SUMA,
    USD_NO_SUMA,
    { titulo: "Conciliación", tipo: "texto" },
  ]
  const vacias = columnas.slice(1).map(() => null)
  const filas: FilaExportable[] = []
  for (const moneda of monedasDe(r.bancos.partidas)) {
    const ps = r.bancos.partidas.filter((p) => p.moneda === moneda)
    filas.push({
      celdas: [
        `${moneda} · ${ps.length} cuenta(s) · saldo ${monto(enCentimos(ps).toFixed(2))} ${moneda}`,
        ...vacias,
      ],
      grupo: true,
    })
    for (const p of ps) {
      filas.push({
        celdas: [
          p.titulo,
          detalleDe(p),
          p.moneda,
          Number(p.monto),
          p.suma ? usdDe(p) : null,
          p.suma ? null : usdDe(p),
          [
            p.conciliacion === null
              ? null
              : (r.estados_conciliacion[p.conciliacion] ?? p.conciliacion),
            p.corte ? `corte ${comoFecha(p.corte)}` : null,
            p.diferencia && Number(p.diferencia) !== 0 ? `dif. ${monto(p.diferencia)}` : null,
          ]
            .filter(Boolean)
            .join(" · ") || null,
        ],
      })
    }
  }
  return { titulo: "Bancos", columnas, filas, pie: pieDe(columnas, r.bancos.partidas) }
}

const operativas = (r: Resumen) => r.cxp.partidas.filter(esOperativa)
const administrativas = (r: Resumen) => r.cxp.partidas.filter((p) => !esOperativa(p))
const ratioDe = (r: Resumen) =>
  r.cobertura.ratio === null ? "—" : `${Number(r.cobertura.ratio).toLocaleString("es-VE")}×`

/** La cuenta del resumen, de arriba abajo: cada `=` resulta de las líneas de encima. */
function seccionCuadro(r: Resumen): Seccion {
  const c = r.cobertura
  const reserva = Number(r.reserva_pct)
  const linea = (concepto: string, valor: string | number, nota: string | null = null) => ({
    celdas: [concepto, Number(valor), nota],
  })
  const resultado = (concepto: string, valor: string | number, nota: string | null = null) => ({
    ...linea(concepto, valor, nota),
    total: true,
  })
  const excedente = Number(c.excedente)
  return {
    titulo: "Alcance de la disponibilidad",
    cuadro: true,
    columnas: [
      { titulo: "Concepto", tipo: "texto" },
      { titulo: "USD", tipo: "monto" },
      { titulo: "De dónde sale", tipo: "texto" },
    ],
    filas: [
      { celdas: ["1 · Disponibilidad total", null, null], grupo: true },
      linea(
        "Bancos",
        r.bancos.total,
        Number(r.sin_conciliar) > 0
          ? `Sólo lo que suma; sin conciliar ${dolares(Number(r.sin_conciliar))}`
          : "Sólo lo que suma; todo conciliado",
      ),
      linea("+ En tránsito y efectivo por recibir", sumaUsd(r.transito.partidas)),
      linea("+ CxC exigible", r.cxc.total, "Cobranza de esta semana"),
      resultado("= Disponibilidad total", c.disponible),
      { celdas: ["2 · Reparto obligatorio", null, null], grupo: true },
      linea(`– Reserva ${reserva} %`, c.reserva, "No se toca"),
      resultado(`= Disponible operativo ${100 - reserva} %`, c.operativo),
      { celdas: ["3 · Compromisos vs. cobertura", null, null], grupo: true },
      linea("– CxP operativas", sumaUsd(queSuman(operativas(r))), "Del 2.0 y de expedientes"),
      linea(
        "– Gasto administrativo por pagar",
        sumaUsd(queSuman(administrativas(r))),
        "Órdenes y gasto administrativo",
      ),
      linea("– Gastos de la semana", r.gastos_semana.total, "Programados y nómina"),
      resultado(
        excedente >= 0 ? "= Excedente" : "= Faltante",
        Math.abs(excedente),
        `Compromisos ${dolares(Number(c.compromisos))}`,
      ),
      { celdas: [`Cobertura ${ratioDe(r)}`, null, `${c.semaforo} · ${c.motivo}`] },
    ],
  }
}

/** Lo que el archivo enseña pero no entra en ninguna cuenta, junto y con su motivo. */
function seccionNoSuma(r: Resumen): Seccion {
  const enCurso = r.cxc.partidas.filter((p) => p.clase === "EN_CURSO")
  const copias = queNoSuman(r.bancos.partidas)
  const candidatas: [string, number, string][] = [
    ["CxC en valija", Number(r.cxc_valija), "Se cobra la semana próxima, al entregarla"],
    ["CxC de servicios en curso", sumaUsd(enCurso), "Aún no es exigible"],
    ["CxP en borrador", Number(r.proyeccion_cxp), "Servicio por culminar: es proyección"],
    ["Bancos: copias del 3.0", sumaUsd(copias), "El mismo dinero ya está en su cuenta del 3.0"],
    [
      "Ingresos sin pagador identificado",
      Number(r.sin_identificar.total),
      "Ya descontados del saldo de su cuenta",
    ],
  ]
  return {
    titulo: "Lo que no suma",
    cuadro: true,
    columnas: [
      { titulo: "Concepto", tipo: "texto" },
      { titulo: "USD", tipo: "monto" },
      { titulo: "Por qué no suma", tipo: "texto" },
    ],
    filas: candidatas.filter(([, v]) => v !== 0).map((f) => ({ celdas: [...f] })),
  }
}

/** Con qué se totalizó: un mismo saldo en Bs da otra cifra con otra tasa. */
export function textoDeTasa(r: Resumen): string {
  const tasa = r.tasas_bs?.find((t) => t.codigo === r.tasa_bs)
  return tasa
    ? `en dólares, con USD y USDT 1:1 y los Bs a ${tasa.nombre} (${monto(tasa.valor)})`
    : "en dólares con la tasa del día"
}

export function subtituloDelResumen(r: Resumen): string {
  return `Al ${comoFecha(r.fecha)}, ${textoDeTasa(r)} · semana del ${comoFecha(r.semana_desde)} al ${comoFecha(r.semana_hasta)}`
}

const SEMAFORO = { VERDE: "Verde", AMARILLO: "Amarillo", ROJO: "Rojo" } as const

/** Las seis cifras que responden «¿me alcanza?», como el panel de la pantalla. */
export function indicadoresDelResumen(r: Resumen): Indicador[] {
  const c = r.cobertura
  const reserva = Number(r.reserva_pct)
  const usd = (valor: string | number) => [{ moneda: "USD", valor: Math.abs(Number(valor)) }]
  return [
    {
      titulo: "Disponibilidad total",
      detalle: "Bancos + tránsito y efectivo + CxC exigible",
      montos: usd(c.disponible),
    },
    { titulo: `Reserva ${reserva} %`, detalle: "No se toca", montos: usd(c.reserva) },
    {
      titulo: `Disponible operativo ${100 - reserva} %`,
      detalle: "Con lo que se cubre la semana",
      montos: usd(c.operativo),
    },
    {
      titulo: "Compromisos",
      detalle: "CxP operativas + gasto administrativo + gastos de la semana",
      montos: usd(c.compromisos),
    },
    {
      titulo: Number(c.excedente) >= 0 ? "Excedente" : "Faltante",
      detalle: "Operativo menos compromisos",
      montos: usd(c.excedente),
    },
    {
      titulo: "Cobertura",
      detalle: `Semáforo ${SEMAFORO[c.semaforo]} · ${c.motivo}`,
      cantidad: ratioDe(r),
    },
  ]
}

export function documentoDelResumen(r: Resumen): DocumentoExportable {
  const detalle: Seccion[] = [
    seccionBancos(r),
    seccionPlana("Tránsito y por recibir", r.transito.partidas),
    seccionAgrupada("Cuentas por cobrar", r.cxc.partidas, r.clases_cxc, "clase", false),
    seccionAgrupada("CxP operativas", operativas(r), r.estados_pago, "estado", true),
    seccionAgrupada(
      "Gasto administrativo por pagar",
      administrativas(r),
      r.estados_pago,
      "estado",
      true,
    ),
    seccionPlana("Gastos de la semana", r.gastos_semana.partidas),
    seccionPlana("Ingresos sin identificar", r.sin_identificar.partidas),
  ].filter((s) => s.filas.length > 0)
  const noSuma = seccionNoSuma(r)
  return {
    titulo: "Resumen Macro",
    subtitulo: subtituloDelResumen(r),
    indicadores: indicadoresDelResumen(r),
    secciones: [
      seccionCuadro(r),
      seccionComposicion(r),
      ...(noSuma.filas.length > 0 ? [noSuma] : []),
      ...detalle,
    ],
  }
}
